import LinkStyled from "@/components/LinkStyled";
import { StrapiImageAsset } from "@/components/StrapiImageAsset";
import { PauseIconCircle, PlayIconCircle } from "@/components/icons";
import {
  actualTrackState,
  playerCommandState,
  playerIsPlayingState,
} from "@/context/states";
import { basierCirleBold, basierCirleRegular } from "@/styles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { CardActionArea, Fade } from "@mui/material";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { useState } from "react";
import { useRecoilState } from "recoil";
import theme from "../styles/theme";
import { BandMessageShareLikeButtons } from "./music/BandMessageShareLikeButtons";

export const MusicResultTableSong = ({
  song,
  variant,
  onPlaySong,
  ref,
  key,
  ...props
}) => {
  const [actualTrack] = useRecoilState(actualTrackState);
  const [playerIsPlaying] = useRecoilState(playerIsPlayingState);
  const [playerCommand, setPlayerCommand] = useRecoilState(playerCommandState);
  const [buttonsOpen, setButtonsOpen] = useState(false);

  const handlePlaySong = (song) => {
    if (actualTrack?.id === song.id) {
      setPlayerCommand({ command: "pause" });
    } else {
      //setPlayerCommand({ command: "play" });
      if (onPlaySong) {
        onPlaySong(song);
      }
    }
  };

  const overlayBox = {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    width: "calc(100% + 4px)",
    height: "calc(100% + 4px)",
    zIndex: "500",
    background:
      actualTrack?.id === song?.id
        ? "linear-gradient(0deg, rgba(245,157,49,0.97) 0%, rgba(245,157,49,0.97) 100%)"
        : "linear-gradient(0deg, rgba(126,105,238,0.97) 0%, rgba(126,105,238,0.97) 100%)",
    border:
      actualTrack?.id === song?.id
        ? `2px solid ${theme.palette.primary.main}`
        : `2px solid ${theme.palette.secondary.main}`,
    transform: "translate(-2px, -2px)",
    [theme.breakpoints.up("sm")]: {
      border: "none",
      transform: "unset",
      width: "100%",
      height: "100%",
    },
  };

  return (
    <Card
      key={key}
      variant={
        actualTrack?.id === song?.id
          ? "PlaylistListCardPlay"
          : "PlaylistListCard"
      }
      elevation={0}
      sx={{
        position: "relative",
        overflow: "visible",
        transition: "all .2s ease-in-out",
        [theme.breakpoints.up("sm")]: {
          border: "none",
          color: theme.palette.common.white,
        },
      }}
    >
      {buttonsOpen ? (
        <Fade in={buttonsOpen}>
          <Box ref={ref} {...props} sx={overlayBox}>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              sx={{ height: "100%" }}
            >
              <BandMessageShareLikeButtons
                actualTrack={actualTrack}
                overlay={true}
                song={song}
              />
            </Grid>
          </Box>
        </Fade>
      ) : (
        <></>
      )}
      <Box
        className="PlaylistListCard--IconMore"
        aria-label="Mehr anzeigen"
        sx={{
          display: { xs: "block", lg: "none" },
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          marginLeft: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          transform: "translate(35%)",
          width: "2.5rem",
          height: "2.5rem",
          border: `2px solid ${theme.palette.common.white}`,
          zIndex: "1000",
          "&.MuiButtonBase-root": {
            minWidth: "auto",
            borderRadius: "0",
          },
        }}
      >
        <ButtonBase
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: "auto",
            width: "1.5rem",
            height: "1.5rem",
          }}
          onClick={() =>
            buttonsOpen ? setButtonsOpen(false) : setButtonsOpen(true)
          }
        >
          {buttonsOpen ? (
            <CloseIcon
              sx={{ fontSize: 32, color: theme.palette.common.white }}
            />
          ) : (
            <MoreHorizIcon
              sx={{ fontSize: 32, color: theme.palette.common.white }}
            />
          )}
        </ButtonBase>
      </Box>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{
          [theme.breakpoints.up("sm")]: {
            pr: 2,
          },
        }}
      >
        <Grid
          item
          xs={12}
          lg={8}
          sx={{
            [theme.breakpoints.up("sm")]: {
              pr: "1rem",
            },
            [theme.breakpoints.up("lg")]: {
              pr: "auto",
            },
          }}
        >
          <Grid container alignItems="center">
            <Grid
              item
              xs="auto"
              sm={5}
              lg={5}
              sx={{
                position: "relative",
                [theme.breakpoints.up("sm")]: {
                  minHeight: "9.625rem",
                },
                [theme.breakpoints.up("xl")]: {
                  minHeight: "8.25rem",
                },
              }}
            >
              <Card
                sx={{
                  display: { xs: "none", sm: "block" },
                }}
              >
                <CardActionArea
                  sx={{
                    position: "absolute",
                    height: "100%",
                    width: "100%",
                  }}
                  onClick={() => {
                    handlePlaySong(song);
                  }}
                >
                  <StrapiImageAsset
                    placeholder="blur"
                    strapiImageObject={song.band?.photo}
                    layout="fill"
                    width={320}
                    height={180}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  ></Box>
                  <IconButton
                    component="div"
                    sx={{
                      position: "absolute",
                      top: "0",
                      right: "0",
                      bottom: "0",
                      left: "0",
                      mx: "auto",
                    }}
                  >
                    <SvgIcon
                      sx={{
                        fontSize: 72,
                        color: theme.palette.common.white,
                        zIndex: "30",
                        transition: "all .2s ease-in-out",
                      }}
                    >
                      {actualTrack?.id === song?.id && playerIsPlaying ? (
                        <PauseIconCircle />
                      ) : (
                        <PlayIconCircle />
                      )}
                    </SvgIcon>
                  </IconButton>
                </CardActionArea>
              </Card>
              <Grid
                container
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: "relative",
                  display: { xs: "block", sm: "none" },
                }}
              >
                <Grid item>
                  <IconButton
                    aria-label="Song Abspielen"
                    className="PlaylistListCard--PlayIcon"
                    onClick={() => {
                      handlePlaySong(song);
                    }}
                  >
                    <SvgIcon
                      sx={{
                        fontSize: 44,
                        color: theme.palette.secondary.main,
                        transition: "all .2s ease-in-out",
                      }}
                    >
                      {actualTrack?.id !== song?.id ? (
                        <PlayIconCircle />
                      ) : (
                        <PauseIconCircle />
                      )}
                    </SvgIcon>
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={9}
              sm={7}
              lg={7}
              sx={{
                textTransform: "uppercase",
                py: 1,
                pr: 1,
                [theme.breakpoints.up("sm")]: {
                  py: 2,
                  pl: 3,
                },
              }}
            >
              <Box
                className="PlaylistListCard--Bandname"
                sx={{
                  fontSize: ".9375rem",
                  fontWeight: basierCirleRegular.style.fontWeight,
                  fontFamily: basierCirleRegular.style.fontFamily,
                  textOverflow: "ellipsis",
                  width: "100%",
                  overflow: "hidden",
                  lineClamp: "2",
                  display: "-webkit-box",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  [theme.breakpoints.up("sm")]: {
                    mb: 0.5,
                    fontSize: "1.375rem",
                  },
                }}
              >
                <LinkStyled
                  href={`/musik/${song?.band?.slug}`}
                  label={song?.band?.name}
                  variant="black"
                />
              </Box>
              <Typography
                className="PlaylistListCard--SongTitle"
                variant="body1"
                sx={{
                  fontSize: ".9375rem",
                  mb: -0.5,
                  fontWeight: basierCirleBold.style.fontWeight,
                  fontFamily: basierCirleBold.style.fontFamily,
                  textOverflow: "ellipsis",
                  width: "100%",
                  overflow: "hidden",
                  lineClamp: "2",
                  display: "-webkit-box",
                  WebkitLineClamp: "2",
                  WebkitBoxOrient: "vertical",
                  [theme.breakpoints.up("sm")]: {
                    mb: 0.5,
                    fontSize: "1.375rem",
                  },
                }}
              >
                {song.title}
              </Typography>
              <Typography
                className="PlaylistListCard--Genre"
                variant="body2"
                sx={{
                  color: grey[900],
                  fontSize: ".625rem",
                  [theme.breakpoints.up("sm")]: {
                    fontSize: "1rem",
                  },
                }}
              >
                {song.band.genres?.[0]?.title} aus {song.band.zipcode?.label}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <BandMessageShareLikeButtons actualTrack={actualTrack} song={song} />
      </Grid>
    </Card>
  );
};
